import { useState } from 'react'
import {
  userDataApi,
  type SharingDTO,
  type UserDataDTO,
  type UserDataExportDTO,
} from '@/shared/api'
import { ConfirmDialog } from '@/shared/ui/ConfirmDialog'

/**
 * LGPD Block D — "Privacidade & dados" panel (Settings → Conta).
 *
 * Minimal, low-risk entry points for the Data Subject Rights API:
 *   - Meus dados                  → GET  /api/v1/user/data
 *   - Exportar meus dados         → POST /api/v1/user/export (client-side download)
 *   - Informações de compartilhamento → GET /api/v1/user/data/sharing
 *   - Solicitar exclusão de conta → POST /api/v1/user/delete-request (NON-DESTRUCTIVE)
 *
 * Loads no tracking scripts. The deletion action is explicit that it only
 * records a request for manual review and deletes nothing immediately.
 */

type Panel = 'none' | 'data' | 'sharing' | 'deleted'

function errMessage(err: unknown): string {
  if (err && typeof err === 'object' && 'message' in err) {
    const m = (err as { message?: unknown }).message
    if (typeof m === 'string' && m.trim()) return m
  }
  return 'Não foi possível concluir a ação. Tente novamente.'
}

function fmtDate(value: string | null | undefined): string {
  if (!value) return '—'
  try {
    return new Date(value).toLocaleString('pt-BR')
  } catch {
    return value
  }
}

export function PrivacyDataSection() {
  const [busy, setBusy] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [panel, setPanel] = useState<Panel>('none')
  const [data, setData] = useState<UserDataDTO | null>(null)
  const [sharing, setSharing] = useState<SharingDTO | null>(null)
  const [deleteMessage, setDeleteMessage] = useState<string | null>(null)
  const [confirmOpen, setConfirmOpen] = useState(false)

  const loadData = async () => {
    setError(null)
    setBusy('data')
    try {
      const result = await userDataApi.getData()
      setData(result)
      setPanel('data')
    } catch (err) {
      setError(errMessage(err))
    } finally {
      setBusy(null)
    }
  }

  const loadSharing = async () => {
    setError(null)
    setBusy('sharing')
    try {
      const result = await userDataApi.getSharing()
      setSharing(result)
      setPanel('sharing')
    } catch (err) {
      setError(errMessage(err))
    } finally {
      setBusy(null)
    }
  }

  const exportData = async () => {
    setError(null)
    setBusy('export')
    try {
      const result: UserDataExportDTO = await userDataApi.export()
      const blob = new Blob([JSON.stringify(result, null, 2)], {
        type: 'application/json',
      })
      const url = URL.createObjectURL(blob)
      const stamp = (result.export_generated_at || '').slice(0, 10) || 'export'
      const a = document.createElement('a')
      a.href = url
      a.download = `rager-meus-dados-${stamp}.json`
      document.body.appendChild(a)
      a.click()
      a.remove()
      URL.revokeObjectURL(url)
    } catch (err) {
      setError(errMessage(err))
    } finally {
      setBusy(null)
    }
  }

  const confirmDeletion = async () => {
    setError(null)
    setBusy('delete')
    try {
      const result = await userDataApi.requestDeletion()
      setDeleteMessage(result.message)
      setPanel('deleted')
    } catch (err) {
      setError(errMessage(err))
    } finally {
      setBusy(null)
      setConfirmOpen(false)
    }
  }

  return (
    <div className="settings-section" data-testid="settings-privacy-data">
      <header className="settings-section-head">
        <h3>Privacidade &amp; dados</h3>
        <p className="settings-section-help">
          Acesse, exporte ou corrija seus dados pessoais e veja com quais
          operadores eles são compartilhados (LGPD, Art. 18). A solicitação de
          exclusão é registrada para revisão manual — nada é apagado
          imediatamente.
        </p>
      </header>

      <div className="settings-form-actions">
        <button
          type="button"
          className="settings-button"
          data-testid="btn-my-data"
          onClick={() => void loadData()}
          disabled={busy !== null}
        >
          {busy === 'data' ? 'Carregando…' : 'Meus dados'}
        </button>
        <button
          type="button"
          className="settings-button"
          data-testid="btn-export-data"
          onClick={() => void exportData()}
          disabled={busy !== null}
        >
          {busy === 'export' ? 'Exportando…' : 'Exportar meus dados'}
        </button>
        <button
          type="button"
          className="settings-button"
          data-testid="btn-sharing"
          onClick={() => void loadSharing()}
          disabled={busy !== null}
        >
          {busy === 'sharing' ? 'Carregando…' : 'Informações de compartilhamento'}
        </button>
        <button
          type="button"
          className="settings-button is-danger"
          data-testid="btn-delete-request"
          onClick={() => setConfirmOpen(true)}
          disabled={busy !== null}
        >
          Solicitar exclusão de conta
        </button>
      </div>

      {error && (
        <p className="settings-section-help" role="alert" data-testid="privacy-error">
          {error}
        </p>
      )}

      {panel === 'data' && data && (
        <div className="settings-card" data-testid="my-data-panel">
          <div>
            <strong>{data.user.full_name || data.user.email}</strong>
          </div>
          <div className="settings-meta">{data.user.email}</div>
          <div className="settings-meta">
            Documentos no workspace: {data.documents.total} · Conversas:{' '}
            {data.conversations.total}
          </div>
          <div className="settings-meta">
            Sessões ativas: {data.security.active_sessions} · Último acesso:{' '}
            {fmtDate(data.security.last_seen_at)}
          </div>
          <div className="settings-meta">
            Consentimento:{' '}
            {data.consent
              ? `${data.consent.action} (política ${data.consent.policy_version})`
              : 'sem registro'}
          </div>
          <div className="settings-meta">
            Organizações:{' '}
            {data.memberships
              .map((m) => `${m.organization_name} (${m.role})`)
              .join(', ') || '—'}
          </div>
          <p className="settings-section-help">
            Use “Exportar meus dados” para baixar o detalhamento completo em JSON.
          </p>
        </div>
      )}

      {panel === 'sharing' && sharing && (
        <div className="settings-card" data-testid="sharing-panel">
          <ul className="settings-sharing-list">
            {sharing.subprocessors.map((s) => (
              <li key={s.key} className="settings-sharing-item">
                <strong>{s.name}</strong>{' '}
                <span className="settings-meta">
                  {s.active ? '· ativo' : '· inativo'}
                </span>
                <div className="settings-meta">{s.purpose}</div>
                {s.notes && <div className="settings-meta">{s.notes}</div>}
              </li>
            ))}
          </ul>
          <p className="settings-section-help">{sharing.notes}</p>
        </div>
      )}

      {panel === 'deleted' && deleteMessage && (
        <div className="settings-card" data-testid="delete-request-result">
          <strong>Pedido de exclusão registrado</strong>
          <p className="settings-section-help">{deleteMessage}</p>
        </div>
      )}

      <ConfirmDialog
        open={confirmOpen}
        title="Solicitar exclusão de conta"
        message={
          'Isto registra um pedido de exclusão para revisão manual durante o ' +
          'beta. Nada é apagado imediatamente — nem documentos, conversas ou ' +
          'backups. Você pode continuar usando a conta normalmente. Deseja ' +
          'registrar o pedido?'
        }
        confirmLabel="Registrar pedido"
        cancelLabel="Cancelar"
        destructive
        busy={busy === 'delete'}
        onConfirm={() => void confirmDeletion()}
        onCancel={() => setConfirmOpen(false)}
      />
    </div>
  )
}
